import { useState, useEffect } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { pathOr } from 'ramda';
import { match } from 'react-router-dom';
import { DocumentNode } from 'graphql';
import gql from 'graphql-tag';

import { useUserMgmt } from 'components/UserMgmt/UserMgmt.context';

type MerchantVenueCounts = {
  merchantName: string;
  venueCounts: {
    venues: number;
    venuesTransacting: number;
    venuesWithTransactions: number;
  };
};

type AccountDetails = {
  merchantUuid: string;
  companyName: string;
  category: string;
  subCategory: string;
  postal: string;
  latitude: number;
  longitude: number;
  numberOfStores: number;
};

type QueryResponse = {
  data: {
    merchantList: MerchantVenueCounts;
    getAccountDetails: AccountDetails;
  };
  loading: boolean;
};

const GET_MERCHANT_DETAILS = gql`
  query GetMerchantDetails(
    $params: MerchantSearchInput
    $accountParams: GetAccountDetailsInput!
  ) {
    merchantList(params: $params) {
      result {
        merchantName
        venueCounts {
          venues
          venuesWithTransactions
        }
      }
    }

    getAccountDetails(params: $accountParams) {
      merchantUuid
      companyName
      category
      subCategory
      postal
      latitude
      longitude
      numberOfStores
    }
  }
`;

const useGetMerchantInfo = (
  match?: match,
  query: DocumentNode = GET_MERCHANT_DETAILS,
) => {
  const { merchantId } = useUserMgmt();
  const [venueCounts, setVenueCounts] = useState<number>(0);
  const [transactingVenueCounts, setTransactingVenueCounts] = useState<number>(
    0,
  );
  const [venuesWithTransactions, setVenuesWithTransactions] = useState<number>(
    0,
  );
  const [
    merchantAcctDetails,
    setMerchantAcctDetails,
  ] = useState<AccountDetails>({} as AccountDetails);

  const merchantUuid = pathOr(merchantId, ['params', 'merchantId'], match);
  const { data: merchantDetails, loading } = useQuery<QueryResponse>(query, {
    variables: {
      params: {
        merchantId: merchantUuid,
      },
      accountParams: {
        merchantUuid,
      },
    },
  });

  useEffect(() => {
    const {
      venues = 0,
      venuesTransacting = 0,
      venuesWithTransactions = 0,
    } = pathOr(
      {},
      ['merchantList', 'result', 0, 'venueCounts'],
      merchantDetails,
    );

    const accountDetails = pathOr({}, ['getAccountDetails'], merchantDetails);

    setVenueCounts(venues);
    setTransactingVenueCounts(venuesTransacting);
    setVenuesWithTransactions(venuesWithTransactions);
    setMerchantAcctDetails(accountDetails);
  }, [merchantDetails]);

  return {
    loading,
    venueCounts,
    transactingVenueCounts,
    venuesWithTransactions,
    merchantAcctDetails,
    merchantDetails,
  };
};

export {
  QueryResponse,
  MerchantVenueCounts,
  AccountDetails,
  useGetMerchantInfo,
};
